/**
 * Authentication Hooks
 *
 * Dual-mode auth: WorkOS AuthKit (when enabled) or legacy localStorage.
 * WorkOS mode uses @workos-inc/authkit-react for session/token management.
 * Legacy mode uses React Query + localStorage (existing behavior).
 */

import { usePostHog } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { z } from 'zod';

import { getAnalyticsEnvironment } from '@/lib/analytics';

import { apiClient, clearAuth } from '../api/client';
import { parseApiError } from '../api/errors';
import type { LoginRequest, LoginResponse, Tenant, User } from './types';
import { LoginResponseSchema, TenantSchema, UserSchema } from './types';
import { isWorkOSEnabled } from './workos-config';

/**
 * Query keys for auth
 */
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  tenant: () => [...authKeys.all, 'tenant'] as const,
};

/**
 * Get current user from localStorage
 */
function getUserFromStorage(): User | null {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;

  try {
    const parsed: unknown = JSON.parse(userStr);
    const result = UserSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

/**
 * Get current tenant from localStorage
 */
function getTenantFromStorage(): Tenant | null {
  const tenantStr = localStorage.getItem('tenant');
  if (!tenantStr) return null;

  try {
    const parsed: unknown = JSON.parse(tenantStr);
    const result = TenantSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated (legacy mode)
 */
function isAuthenticatedLegacy(): boolean {
  const user = getUserFromStorage();
  return !!user;
}

/**
 * Get the authentication method used for the current session.
 * Returns 'workos' or 'legacy' based on how the user logged in.
 */
function getAuthMethod(): 'workos' | 'legacy' | null {
  return localStorage.getItem('authMethod') as 'workos' | 'legacy' | null;
}

interface RestoredSession {
  csrfToken: string;
  user: User;
  tenant: Tenant;
}

let sessionRestorePromise: Promise<RestoredSession | null> | null = null;

async function restoreWorkOSSession(): Promise<RestoredSession | null> {
  sessionRestorePromise ??= apiClient
    .get<unknown>('/identity/auth/me')
    .then((response) => {
      const result = z
        .object({ csrfToken: z.string(), user: UserSchema, tenant: TenantSchema })
        .safeParse(response.data);
      if (!result.success) return null;

      localStorage.setItem('csrfToken', result.data.csrfToken);
      localStorage.setItem('user', JSON.stringify(result.data.user));
      localStorage.setItem('tenant', JSON.stringify(result.data.tenant));
      localStorage.setItem('authMethod', 'workos');
      return result.data;
    })
    .catch(() => null)
    .finally(() => {
      sessionRestorePromise = null;
    });

  return sessionRestorePromise;
}

/**
 * Hook to get current user (legacy)
 */
export function useUser() {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => getUserFromStorage() ?? (await restoreWorkOSSession())?.user ?? null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Hook to get current tenant (legacy)
 */
export function useTenant() {
  return useQuery({
    queryKey: authKeys.tenant(),
    queryFn: async () => getTenantFromStorage() ?? (await restoreWorkOSSession())?.tenant ?? null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Unified auth hook — works with both WorkOS and legacy auth.
 *
 * When WorkOS is enabled:
 * - User/session comes from AuthKitProvider (useAuth from @workos-inc/authkit-react)
 * - Permissions come from JWT via WorkOS useAuth().permissions
 * - Token management is handled by AuthKit (no manual localStorage)
 *
 * When WorkOS is disabled:
 * - User comes from localStorage (existing behavior)
 * - Permissions come from user.permissions in localStorage
 * - Token management is manual via localStorage
 */
export function useAuth() {
  // Legacy auth (always available)
  const { data: legacyUser, isLoading: isLoadingUser } = useUser();
  const { data: tenant, isLoading: isLoadingTenant } = useTenant();

  // WorkOS auth — always called (AuthKitProvider always wraps the app).
  // When WorkOS is disabled or user hasn't signed in via WorkOS,
  // workosAuth.user will be null and we fall through to legacy.
  const workosAuth = useWorkOSAuth();

  if (isWorkOSEnabled() && workosAuth.user) {
    const storedUser = legacyUser;
    const workosUser: User = {
      userId: storedUser?.userId ?? workosAuth.user.id,
      email: storedUser?.email ?? workosAuth.user.email ?? '',
      name:
        storedUser?.name ??
        [workosAuth.user.firstName, workosAuth.user.lastName].filter(Boolean).join(' ') ??
        workosAuth.user.email ??
        '',
      tenantId: storedUser?.tenantId ?? workosAuth.organizationId ?? '',
      permissions:
        storedUser?.permissions && storedUser.permissions.length > 0
          ? storedUser.permissions
          : (workosAuth.permissions ?? []),
      role: storedUser?.role ?? workosAuth.role ?? undefined,
      roles: storedUser?.roles,
    };

    return {
      user: workosUser,
      tenant,
      isAuthenticated: true,
      isLoading: workosAuth.isLoading,
      authMethod: 'workos' as const,
      // Expose WorkOS permissions directly for usePermissions() hook
      workosPermissions: workosAuth.permissions ?? [],
      // WorkOS auth methods
      signIn: workosAuth.signIn,
      signOut: workosAuth.signOut,
      getAccessToken: workosAuth.getAccessToken,
    };
  }

  // Legacy mode
  return {
    user: legacyUser,
    tenant,
    isAuthenticated: isAuthenticatedLegacy(),
    isLoading: isLoadingUser || isLoadingTenant,
    authMethod: getAuthMethod(),
    workosPermissions: null,
    signIn: undefined,
    signOut: undefined,
    getAccessToken: undefined,
  };
}

/**
 * Hook to get the current authentication method.
 * Returns 'workos', 'legacy', or null if not logged in.
 */
export function useAuthMethod(): 'workos' | 'legacy' | null {
  const { authMethod } = useAuth();
  return authMethod;
}

/**
 * Hook to login (legacy email/password)
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await apiClient.post<LoginResponse>(
        '/identity/auth/workos/login',
        credentials,
      );

      // Validate response
      const validated = LoginResponseSchema.parse(response.data);
      return validated;
    },
    onSuccess: (data) => {
      localStorage.setItem('csrfToken', data.csrfToken);

      // Store user and tenant
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('tenant', JSON.stringify({ id: data.user.tenantId }));

      // Update React Query cache
      queryClient.setQueryData(authKeys.user(), data.user);
      queryClient.setQueryData(authKeys.tenant(), { id: data.user.tenantId });

      // Identify user in PostHog for analytics
      const environment = getAnalyticsEnvironment();
      posthog?.identify(data.user.userId, {
        email: data.user.email,
        name: data.user.name,
        tenantId: data.user.tenantId,
        environment,
      });
    },
    onError: (error) => {
      const apiError = parseApiError(error);
      console.error('[useLogin] Login failed:', apiError.message);
    },
  });
}

/**
 * Hook to logout
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: async () => {
      try {
        await apiClient.post('/identity/auth/logout');
      } finally {
        clearAuth();
        localStorage.removeItem('authMethod');
      }
    },
    onSettled: () => {
      // Reset PostHog to unlink future events from this user
      posthog?.reset();

      // Clear React Query cache
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.setQueryData(authKeys.tenant(), null);
      queryClient.clear();

      // Redirect to login
      window.location.href = '/login';
    },
  });
}

/**
 * Hook to validate token
 * Useful for checking if token is still valid
 */
export function useValidateToken() {
  return useQuery({
    queryKey: [...authKeys.all, 'validate'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/identity/permissions');
        return response.status !== 401;
      } catch {
        return false;
      }
    },
    enabled: isAuthenticatedLegacy(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}
